import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, Check, IndianRupee, TriangleAlert, Gauge } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { loanProfilingApi } from '@/services/api';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const ceilingFor = (age, ceilings = {}) => {
  if (age == null) return null;
  if (age <= 3) return ceilings.ltv_0_3_years ?? 85;
  if (age <= 5) return ceilings.ltv_3_5_years ?? 80;
  if (age <= 7) return ceilings.ltv_5_7_years ?? 75;
  if (age <= 10) return ceilings.ltv_7_10_years ?? 70;
  return ceilings.ltv_10_plus_years ?? 60;
};

/** What the customer asks for, checked against what the car can carry. */
export const LoanAmountBlock = ({ leadId, caseProfile = {}, vehicle = {}, ltvCeilings, onChanged }) => {
  const [amount, setAmount] = useState(caseProfile.required_loan_amount ?? '');
  const [roi, setRoi] = useState(caseProfile.expected_roi ?? '');
  const [emi, setEmi] = useState(caseProfile.expected_emi ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setAmount(caseProfile.required_loan_amount ?? ''); }, [caseProfile.required_loan_amount]);
  useEffect(() => { setRoi(caseProfile.expected_roi ?? ''); }, [caseProfile.expected_roi]);
  useEffect(() => { setEmi(caseProfile.expected_emi ?? ''); }, [caseProfile.expected_emi]);

  const { valuation, maxEligible, ltv, overLimit, cap } = useMemo(() => {
    const val = Number(vehicle.vehicle_valuation || vehicle.purchase_cost || 0);
    const age = vehicle.manufacture_year ? new Date().getFullYear() - Number(vehicle.manufacture_year) : null;
    const ceiling = ceilingFor(age, ltvCeilings);
    const max = val && ceiling ? Math.round((val * ceiling) / 100) : null;
    const asked = Number(amount || 0);
    return {
      valuation: val,
      maxEligible: max,
      cap: ceiling,
      ltv: val && asked ? Math.round((asked / val) * 1000) / 10 : null,
      overLimit: !!(max && asked && asked > max),
    };
  }, [amount, vehicle, ltvCeilings]);

  const save = async () => {
    setSaving(true);
    try {
      await loanProfilingApi.saveCaseProfile(leadId, {
        required_loan_amount: amount === '' || amount == null ? null : parseFloat(amount),
        expected_roi: roi === '' || roi == null ? null : parseFloat(roi),
        expected_emi: emi === '' || emi == null ? null : parseFloat(emi),
      });
      toast.success('Loan requirement saved');
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error('Could not save the loan amount');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className="mt-6 mb-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="loan-amount-block"
    >
      <div className="flex flex-col gap-1 mb-4">
        <h4 className="text-sm font-semibold text-slate-900">Loan requirement</h4>
        <p className="text-xs text-slate-500">
          What the customer asked for on the call. Checked against the car's value — never blocked.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div className="flex flex-col space-y-1.5">
          <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Required loan amount (₹)</Label>
          <div className="relative">
            <IndianRupee className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="number" min={0} value={amount ?? ''}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 500000"
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              data-testid="required-loan-amount"
            />
          </div>
        </div>

        <div className="md:col-span-2 flex flex-col gap-2">
          {valuation > 0 ? (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
              <span className="text-slate-500">
                Car value <span className="font-semibold text-slate-800" data-testid="loan-amount-valuation">{inr(valuation)}</span>
              </span>
              {maxEligible && (
                <span className="text-slate-500">
                  Max eligible <span className="font-semibold text-slate-800" data-testid="loan-amount-max">{inr(maxEligible)}</span>
                </span>
              )}
              {ltv != null && (
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <Gauge className="w-3.5 h-3.5" />LTV <span className="font-semibold text-slate-800" data-testid="loan-amount-ltv">{ltv}%</span>
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400" data-testid="loan-amount-no-valuation">
              Add the car valuation on the vehicle below to see how much it can carry.
            </p>
          )}

          {overLimit && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              data-testid="loan-amount-over-limit">
              <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                The ask is {inr(Number(amount) - maxEligible)} above the {cap}% ceiling for this
                car's age — still fine to proceed, banks often adjust the valuation.
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 mt-4 border-t border-slate-100" data-testid="customer-ask-block">
        <div className="md:col-span-2">
          <h5 className="text-sm font-semibold text-slate-900">What the customer is willing to close at</h5>
          <p className="text-xs text-slate-500">The brief we negotiate the banks down to — shown on every offer.</p>
        </div>
        <div className="flex flex-col space-y-1.5">
          <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Expected rate of interest (%)</Label>
          <Input type="number" step="0.05" min={0} value={roi ?? ''} onChange={(e) => setRoi(e.target.value)}
            placeholder="e.g. 12.5"
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            data-testid="expected-roi-input" />
        </div>
        <div className="flex flex-col space-y-1.5">
          <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">EMI he is willing to pay (₹ / month)</Label>
          <div className="relative">
            <IndianRupee className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input type="number" min={0} value={emi ?? ''} onChange={(e) => setEmi(e.target.value)}
              placeholder="e.g. 20000"
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              data-testid="expected-emi-input" />
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 mt-4 border-t border-slate-100">
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-loan-amount-button">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>    </motion.div>
  );
};

export default LoanAmountBlock;
